import { Loader } from 'components/Loader/Loader';
import { UgPager } from 'components/UgPager/UgPager';
import { AccountStatementRow } from 'services/api/accountStatementService';
import styles from './AccountStatement.module.css';
import { useTranslation } from 'react-i18next';
import { STATEMENT_TX_TYPES, txTypeLabel } from 'helpers/txTypes';

interface Props {
  rows: AccountStatementRow[];
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedPeriod: string;
  onSelectedPeriodChange: (value: string) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  transactionType: string;
  onTransactionTypeChange: (value: string) => void;
  onOpenSlip: (ticketId: number | null) => void;
}

export const AccountStatementMobile = ({
  rows,
  loading,
  page,
  totalPages,
  onPageChange,
  selectedPeriod,
  onSelectedPeriodChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  transactionType,
  onTransactionTypeChange,
  onOpenSlip,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <label>
          {t('select_period_label')}
          <select
            value={selectedPeriod}
            onChange={(event) => onSelectedPeriodChange(event.target.value)}
          >
            <option value="0">{t('today_label')}</option>
            <option value="3">{t('last_3_days_label')}</option>
            <option value="7">{t('last_7_days_label')}</option>
            <option value="14">{t('last_14_days_label')}</option>
            <option value="30">{t('last_30_days_label')}</option>
            <option value="60">{t('last_60_days_label')}</option>
            <option value="custom_date">{t('another_period_label')}</option>
          </select>
        </label>

        {selectedPeriod === 'custom_date' && (
          <>
            <label>
              {t('start_date_label')}
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
              />
            </label>
            <label>
              {t('end_date_label')}
              <input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
              />
            </label>
          </>
        )}

        <label>
          {t('tx_type_select_label')}
          <select
            value={transactionType}
            onChange={(event) => onTransactionTypeChange(event.target.value)}
          >
            <option value="all">{t('tx_all_types')}</option>
            {STATEMENT_TX_TYPES.map((type) => (
              <option key={type} value={type}>
                {txTypeLabel(t, type)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className={styles.listHeader}>
        <span>ID</span>
        <span>{t('tx_type_label')}</span>
        <span>{t('tx_amount_label')}</span>
      </div>

      {loading && <Loader />}
      {!loading &&
        rows.map((row) => (
          <div
            key={row.id}
            className={styles.row}
            onClick={() => onOpenSlip(row.ticket_id)}
            role={row.ticket_id ? 'button' : undefined}
            tabIndex={row.ticket_id ? 0 : -1}
            title={row.ticket_id ? 'Show details' : undefined}
          >
            <div className={styles.main}>
              <span>{row.ticket_id ?? row.id}</span>
              <span>
                {txTypeLabel(t, row.transaction_type)}
                {row.casino_game_name ? ` (${row.casino_game_name})` : ''}
              </span>
              <span>{row.amount.toFixed(2)}</span>
            </div>
            <div className={styles.sub}>
              <span>{row.timestamp}</span>
              <span>Balance: {row.total_balance.toFixed(2)}</span>
              <span className={styles.chevron}>{row.ticket_id ? '›' : ''}</span>
            </div>
          </div>
        ))}

      <div className={styles.pager}>
        <UgPager
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
};
